import React, { createContext, useContext, useState, ReactNode } from "react";
import CustomAlert from "../custom-alert/CustomAlert";

type AlertType = "info" | "confirm" | "success" | "error";

interface AlertOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface AlertContextProps {
  info: (options: AlertOptions) => void;
  confirm: (options: AlertOptions) => void;
  success: (options: AlertOptions) => void;
  error: (options: AlertOptions) => void;
}

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) throw new Error("useAlert must be used within AlertProvider");
  return context;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [show, setShow] = useState(false);
  const [options, setOptions] = useState<AlertOptions>({message: ""});
  const [type, setType] = useState<AlertType>("info");

  const handleClose = () => {
    setShow(false);
  };

  const showAlert = (type: AlertType, opts: AlertOptions) => {
    const defaultTitles: Record<AlertType, string> = {
      info: "แจ้งเตือน",
      confirm: "ยืนยัน",
      success: "สำเร็จ",
      error: "ผิดพลาด",
    };

    setOptions({
      title: opts.title || defaultTitles[type],
      message: opts.message,
      confirmText: opts.confirmText || "ตกลง",
      cancelText: type === "confirm" ? opts.cancelText || "ยกเลิก" : undefined,
      onConfirm: () => {
        opts.onConfirm?.();
        handleClose();
      },
      onCancel: type === "confirm" ? () => {
        opts.onCancel?.();
        handleClose();
      } : undefined,
    });

    setType(type);
    setShow(true);
  };

  const info = (opts: AlertOptions) => showAlert("info", opts);
  const confirm = (opts: AlertOptions) => showAlert("confirm", opts);
  const success = (opts: AlertOptions) => showAlert("success", opts);
  const error = (opts: AlertOptions) => showAlert("error", opts);

  return (
    <AlertContext.Provider value={{ info, confirm, success, error }}>
      {children}
      <CustomAlert
        title={options.title}
        message={options.message || ""}
        confirmText={options.confirmText || "ตกลง"}
        cancelText={type === "confirm" ? options.cancelText : undefined}
        onConfirm={options.onConfirm || handleClose}
        onCancel={type === "confirm" ? options.onCancel : undefined}
        show={show}
      />
    </AlertContext.Provider>
  );
};
